import React, { useState, useEffect, useMemo } from 'react'
import { TimePicker } from 'antd'
import moment from 'moment'
import PropTypes from 'prop-types'

const TimeSelector = ({
  dateString,
  onChange,
  localeFormat = 'DD/MM/YYYY',
  disabled = false,
  minuteStep = 1,
  restrictToCurrentTime = true,
}) => {
  const [time, setTime] = useState(undefined)

  useEffect(() => {
    if (dateString) {
      const parsed = moment(dateString, localeFormat, true)
      if (parsed.isValid()) {
        setTime(parsed)
      }
    }
  }, [dateString, localeFormat])

  useEffect(() => {
    if (!dateString) {
      setTime(undefined)
    }
  }, [dateString])

  const handleTimeChange = (timeMoment) => {
    setTime(timeMoment)

    const combined = moment(dateString, localeFormat)
      .hour(timeMoment.hour())
      .minute(timeMoment.minute())
      .second(0)
      .millisecond(0)

    onChange?.(combined.format(localeFormat))
  }

  const { disabledHours, disabledMinutes } = useMemo(() => {
    if (!restrictToCurrentTime || !dateString) {
      return { disabledHours: () => [], disabledMinutes: () => [] }
    }

    const selectedDate = moment(dateString, localeFormat)
    const today = moment()

    if (!selectedDate.isSame(today, 'day')) {
      return { disabledHours: () => [], disabledMinutes: () => [] }
    }

    return {
      disabledHours: () => {
        const hours = []
        for (let i = today.hour() + 1; i < 24; i++) {
          hours.push(i)
        }
        return hours
      },
      disabledMinutes: (selectedHour) => {
        if (selectedHour === today.hour()) {
          const minutes = []
          for (let i = today.minute() + 1; i < 60; i++) {
            minutes.push(i)
          }
          return minutes
        }
        return []
      },
    }
  }, [dateString, localeFormat, restrictToCurrentTime])

  return (
    <TimePicker
      value={time}
      onChange={handleTimeChange}
      format="HH:mm"
      disabled={disabled || !dateString}
      minuteStep={minuteStep}
      placeholder="00:00"
      disabledHours={disabledHours}
      disabledMinutes={disabledMinutes}
      allowClear={false}
    />
  )
}

TimeSelector.propTypes = {
  dateString: PropTypes.string,
  onChange: PropTypes.func,
  localeFormat: PropTypes.string,
  disabled: PropTypes.bool,
  minuteStep: PropTypes.number,
  restrictToCurrentTime: PropTypes.bool,
}

export default TimeSelector
